"use client";

/**
 * Formulaire de capture de lead RÉUTILISABLE et BAS-FRICTION (QuickLeadForm).
 *
 * Surface de conversion ON-PAGE pour les pages à forte intention qui, jusqu'ici,
 * ne faisaient que LIER vers /reservation : hubs et pages chauffeur (VTC) ET
 * pages location voiture (ville / prestige). Le lead chaud est capturé SUR la
 * page (zéro friction), pré-rempli avec le service / la ville courante.
 *
 * Un seul composant, deux contextes via la prop `context` :
 *  - "chauffeur" → sujet « Demande chauffeur — {service} », locationType
 *    "chauffeur".
 *  - "location"  → sujet « Demande location — {service} », locationType
 *    "location_lead".
 *
 * Fin wrapper au-dessus de <LeadForm> (coquille + pipeline d'envoi PARTAGÉS) :
 * validation client (react-hook-form), honeypot anti-spam, locale dans le body,
 * POST vers /api/contact, états loading / success / error. Aucune nouvelle
 * mécanique de fetch. Volontairement compact (peu de champs) — c'est un quick
 * form bas-de-funnel. Seuls les champs, le sujet/message, le `locationType` et
 * les chaînes localisées sont fournis ici ; tout le reste est mutualisé.
 *
 * Bilingue via la prop `locale` (défaut "fr"). Toutes les chaînes visibles
 * viennent de QUICK_LEAD_UI[locale]. L'ancre de section id={anchorId} (défaut
 * QUICK_LEAD_FORM_ID = "demande") est importée depuis quickLeadFormUi.ts (module
 * neutre — règle RSC) et sert de cible aux CTA repointés des vues serveur.
 */
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { PHONE_PATTERNS } from "@/config/forms";
import LeadForm, {
  LEAD_INPUT_CLASS,
  LEAD_LABEL_CLASS,
} from "@/components/forms/LeadForm";
import {
  QUICK_LEAD_UI,
  QUICK_LEAD_FORM_ID,
  type QuickLeadContext,
} from "./quickLeadFormUi";

type QuickLeadFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  date: string;
  need: string;
  message: string;
  honeypot?: string;
};

const QUICK_LEAD_DEFAULTS: QuickLeadFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  date: "",
  need: "",
  message: "",
  honeypot: "",
};

export type QuickLeadFormProps = {
  locale?: Locale;
  /** Pilote sujet équipe (FR), locationType envoyé et quelques libellés. */
  context: QuickLeadContext;
  /** Service / ville de la page, préfixé dans le sujet + le message. */
  serviceLabel?: string;
  /** Ancre de section (cible des CTA). Défaut : QUICK_LEAD_FORM_ID ("demande"). */
  anchorId?: string;
};

export default function QuickLeadForm({
  locale = "fr",
  context,
  serviceLabel,
  anchorId = QUICK_LEAD_FORM_ID,
}: QuickLeadFormProps): JSX.Element {
  const ui = tr(QUICK_LEAD_UI, locale);

  const inputClass = LEAD_INPUT_CLASS;
  const labelClass = LEAD_LABEL_CLASS;

  return (
    <LeadForm<QuickLeadFormData>
      locale={locale}
      anchorId={anchorId}
      submitIcon="mdi:calendar-check"
      honeypotId="quick-lead-honeypot"
      defaultValues={QUICK_LEAD_DEFAULTS}
      resetValues={QUICK_LEAD_DEFAULTS}
      ui={{
        eyebrow: ui.eyebrow,
        heading: ui.heading(context, serviceLabel),
        subtitle: ui.subtitle(context, serviceLabel),
        honeypotLabel: ui.honeypotLabel,
        submit: ui.submit,
        sending: ui.sending,
        successMessage: ui.successMessage,
        errorMessage: ui.errorMessage,
        consentNote: ui.consentNote,
        privacyNote: ui.privacyNote,
      }}
      intro={
        // Pastille de réassurance contextuelle.
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 font-medium text-black">
            <Icon
              icon={context === "chauffeur" ? "mdi:steering" : "mdi:car"}
              className="h-4 w-4 text-primary"
            />
            {ui.badge(context, serviceLabel)}
          </span>
        </div>
      }
      buildSubmission={(values) => {
        const date = values.date.trim();
        const need = values.need.trim();

        // Sujet équipe (FR — l'équipe lit le FR quelle que soit la langue du
        // visiteur). Le préfixe ("Demande chauffeur" / "Demande location") est
        // détecté côté API via locationType, jamais via le sujet.
        const subject =
          context === "chauffeur"
            ? `Demande chauffeur — ${serviceLabel ?? "Côte d'Azur"}`
            : `Demande location — ${serviceLabel ?? "KS Rent Services"}`;

        // Message : saisie libre, sinon défaut sensé (≥ 5 car. requis côté API).
        // Le besoin libre est replié dans le message par défaut quand
        // l'utilisateur n'écrit rien dans la zone de message.
        const typedMessage = values.message.trim();
        const message =
          typedMessage.length >= 5
            ? need
              ? `${typedMessage}\n${ui.needLabel(context)}: ${need}`
              : typedMessage
            : ui.defaultMessage({ context, serviceLabel, need, date });

        return {
          subject,
          message,
          locationType: context === "chauffeur" ? "chauffeur" : "location_lead",
          extraPayload: {
            date: date || undefined,
          },
        };
      }}
      fields={(register, errors) => (
        <>
          {/* Prénom */}
          <label className="block">
            <span className={labelClass}>{ui.firstNameLabel}</span>
            <input
              className={inputClass}
              placeholder={ui.firstNamePlaceholder}
              {...register("firstName", {
                required: ui.requiredFirstName,
                minLength: { value: 2, message: ui.requiredFirstName },
              })}
            />
            {errors.firstName && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.firstName.message}
              </span>
            )}
          </label>

          {/* Nom */}
          <label className="block">
            <span className={labelClass}>{ui.lastNameLabel}</span>
            <input
              className={inputClass}
              placeholder={ui.lastNamePlaceholder}
              {...register("lastName", {
                required: ui.requiredLastName,
                minLength: { value: 2, message: ui.requiredLastName },
              })}
            />
            {errors.lastName && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.lastName.message}
              </span>
            )}
          </label>

          {/* Email */}
          <label className="block">
            <span className={labelClass}>{ui.emailLabel}</span>
            <input
              type="email"
              className={inputClass}
              placeholder={ui.emailPlaceholder}
              {...register("email", { required: ui.requiredEmail })}
            />
            {errors.email && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.email.message}
              </span>
            )}
          </label>

          {/* Téléphone (international, facultatif) */}
          <label className="block">
            <span className={labelClass}>{ui.phoneLabel}</span>
            <input
              type="tel"
              className={inputClass}
              placeholder={ui.phonePlaceholder}
              {...register("phone", {
                pattern: {
                  value: PHONE_PATTERNS.intl,
                  message: ui.invalidPhone,
                },
              })}
            />
            {errors.phone && (
              <span className="mt-1 block text-xs text-red-600">
                {errors.phone.message}
              </span>
            )}
          </label>

          {/* Date souhaitée (facultative) */}
          <label className="block">
            <span className={labelClass}>{ui.dateLabel}</span>
            <input type="date" className={inputClass} {...register("date")} />
          </label>

          {/* Besoin libre (facultatif) — bas-friction, remplace les passagers */}
          <label className="block">
            <span className={labelClass}>{ui.needLabel(context)}</span>
            <input
              className={inputClass}
              placeholder={ui.needPlaceholder(context)}
              {...register("need")}
            />
          </label>

          {/* Message (facultatif) */}
          <label className="block md:col-span-2">
            <span className={labelClass}>{ui.messageLabel}</span>
            <textarea
              rows={4}
              maxLength={900}
              className="w-full resize-y rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-black outline-none ring-primary/40 transition focus:ring-2"
              placeholder={ui.messagePlaceholder(context)}
              {...register("message")}
            />
          </label>
        </>
      )}
    />
  );
}
